import { useState, useCallback, useMemo } from "react";
import { ACCENT_THEMES, DEFAULT_ACCENT_ID, ACCENT_THEME_STORAGE_KEY } from "../themes/accentThemes";

export function applyAccentTheme(id: string): void {
  const theme = ACCENT_THEMES[id] || ACCENT_THEMES[DEFAULT_ACCENT_ID];
  const root = document.documentElement;
  root.style.setProperty("--accent", theme.primary);
  root.style.setProperty("--accent-hover", theme.hover);
  root.style.setProperty("--accent-glow", theme.glow);
  root.style.setProperty("--accent-soft", theme.soft);
  root.style.setProperty("--accent-gradient-dark", theme.gradientDark);
  root.style.setProperty("--accent-gradient-light", theme.gradientLight);
}

export function useAccentTheme() {
  const [accentId, setAccentIdState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(ACCENT_THEME_STORAGE_KEY);
      return saved && ACCENT_THEMES[saved] ? saved : DEFAULT_ACCENT_ID;
    } catch {
      return DEFAULT_ACCENT_ID;
    }
  });

  const setAccentTheme = useCallback((id: string) => {
    if (!ACCENT_THEMES[id]) return;
    setAccentIdState(id);
    applyAccentTheme(id);
    try {
      localStorage.setItem(ACCENT_THEME_STORAGE_KEY, id);
    } catch {
      // ignore
    }
  }, []);

  // Sync from server — call this when account data loads
  const syncFromServer = useCallback((serverAccentId: string | null | undefined) => {
    const id = serverAccentId && ACCENT_THEMES[serverAccentId] ? serverAccentId : DEFAULT_ACCENT_ID;
    setAccentIdState(id);
    applyAccentTheme(id);
    try {
      localStorage.setItem(ACCENT_THEME_STORAGE_KEY, id);
    } catch {
      // ignore
    }
  }, []);

  const themes = useMemo(() => Object.values(ACCENT_THEMES), []);

  return { accentId, setAccentTheme, syncFromServer, themes };
}
